import usePeriodLevels from "backend/hooks/model/usePeriodLevels";
import usePeriods from "backend/hooks/model/usePeriods";
import { ICardElementWithChildren, IPeriod } from "backend/model/model";
import getValues from "backend/values";
import {
  Tbl,
  TblBody,
  TblBodyCell,
  TblBodyRow,
  TblHead,
  TblHeadCell,
  TblHeadRow,
} from "design/components/tables/ValueTable";
import { t } from "i18next";
import * as conf from "qpr-base/configuration";
import { IValueTableSettings } from "qpr-base/configuration/types";
import React from "react";
import { useQuery } from "react-query";

const getValueTablePeriods = (
  element: ICardElementWithChildren | undefined,
  periods: IPeriod[] | undefined,
  settings: IValueTableSettings
): IPeriod[] => {
  if (!element || !periods) return [];

  let currentIndex = 0;
  for (let i = 0; i < periods.length; i += 1) {
    if (periods[i].name === element.periodname) currentIndex = i;
  }
  const periodsBeforeActual = settings.periodsBeforeActual || 9;
  const periodsAfterActual = settings.periodsAfterActual || 2;

  const startIndex = Math.max(currentIndex - periodsBeforeActual, 0);
  const endIndex = Math.min(
    Number(currentIndex) + Number(periodsAfterActual),
    periods.length - 1
  );
  const chartPeriods: IPeriod[] = [];
  for (let i = startIndex; i <= endIndex; i += 1) chartPeriods.push(periods[i]);

  return chartPeriods;
};

const ValueHistory = (props: { element: ICardElementWithChildren }) => {
  const { data: periods } = usePeriods(
    props.element.periodlevel || props.element.periodlevelid
  );
  const { data: periodLevels } = usePeriodLevels();

  const { data: values, isLoading: valuesLoading } = useQuery(
    `valuehistory-${props.element.id}-${JSON.stringify(periods)}`,
    async () => {
      let valueTablePeriods;
      if (periods) {
        valueTablePeriods = getValueTablePeriods(
          props.element,
          periods,
          conf.getKPIValueTableSettings(props.element)
        );
      }
      return valueTablePeriods
        ? await getValues(
            props.element.id,
            valueTablePeriods,
            false,
            periodLevels!
          )
        : [];
    },
    {
      enabled: !!periodLevels,
    }
  );

  const [usedRows, valueTableSeriesSettings] = React.useMemo(() => {
    return [
      values || [],
      values?.length
        ? conf.getKPIValueTableSettings(values[0])
        : { series: [] },
    ];
  }, [periods, values]);

  const getTableRow = (
    series: string,
    showRangeColors: boolean | undefined
  ) => {
    if (!showRangeColors) {
      return (
        <TblBodyRow>
          <TblBodyCell>{t(`configtypes.${series}`)}</TblBodyCell>
          {usedRows.map((value) => (
            <TblBodyCell key={value.period?.name}>
              {value[
                `${series}_prettyvalue` as keyof ICardElementWithChildren
              ] || ""}
            </TblBodyCell>
          ))}
        </TblBodyRow>
      );
    }

    return (
      <TblBodyRow>
        <TblBodyCell component="th" scope="row">
          {t(`configtypes.${series}`)}
        </TblBodyCell>
        {usedRows.map((value) => {
          const rangeColor =
            `${series}_rangecolor` as keyof ICardElementWithChildren;
          return (
            <TblBodyCell
              key={value.period?.name}
              style={{
                color: `#${value[rangeColor]}`,
                fontWeight: 500,
                background: `linear-gradient(#${value[rangeColor]}11, #${value[rangeColor]}11)`,
              }}
            >
              {value[
                `${series}_prettyvalue` as keyof ICardElementWithChildren
              ] || ""}
            </TblBodyCell>
          );
        })}
      </TblBodyRow>
    );
  };

  return (
    <div>
      {values && values.length > 0 ? (
        <Tbl>
          <TblHead>
            <TblHeadRow>
              <TblHeadCell>{t("kpis.Value Name")}</TblHeadCell>
              {usedRows.map((value) => (
                <TblHeadCell key={value.period?.name}>
                  {value.period?.name}
                </TblHeadCell>
              ))}
            </TblHeadRow>
          </TblHead>

          <TblBody>
            {valueTableSeriesSettings.series?.map((setting) =>
              getTableRow(setting.symbol, setting.showRangeColors)
            )}
          </TblBody>
        </Tbl>
      ) : null}
    </div>
  );
};

export default ValueHistory;
